@import '../../utils/control-density.css';
@import '../../utils/chrome-layout.css';
@import '../../utils/focus-ring.css';
@import '../../utils/scrollbar-hidden.css';

:host {
  display: block;
  flex: 0 0 auto;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  --_panel-tools-rail-width: var(--dimension-size-600);
  --_panel-tools-drawer-width: var(--ds-shell-panel-tools-width, var(--dimension-panel-width-xs));
  --_panel-tools-border: var(--dimension-stroke-width-012, 1px) solid var(--color-border-tertiary);
  /* Match panel-nav expand timing (medium-1); easing differs by direction. */
  --_panel-tools-expand-dur: var(--effect-animation-duration-medium-1, 300ms);
}

.panel-tools__layout {
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
}

/* Clip frame — only max-width animates (slide reveal, same as pre-rail :host). */
.panel-tools__drawer {
  flex: 0 0 auto;
  max-width: 0;
  min-width: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
  transition: none;
}

:host(.panel-tools--ready) .panel-tools__drawer {
  transition: max-width var(--_panel-tools-expand-dur)
    var(--effect-animation-easing-ease-in-out, ease);
}

:host(.panel-tools--ready.panel-tools--motion-opening) .panel-tools__drawer {
  transition: max-width var(--_panel-tools-expand-dur)
    var(--effect-animation-easing-ease-out, ease-out);
}

:host(.panel-tools--ready.panel-tools--motion-closing) .panel-tools__drawer {
  transition: max-width var(--_panel-tools-expand-dur)
    var(--effect-animation-easing-ease-in, ease-in);
}

:host(.panel-tools--open) .panel-tools__drawer {
  max-width: var(--_panel-tools-drawer-width);
}

.panel-tools__drawer--visible {
  border-left: var(--_panel-tools-border);
}

/* Fixed-width surface — ShellApp supplies the standard 300px tools width. */
.panel-tools__drawer-surface {
  display: flex;
  flex-direction: column;
  width: var(--_panel-tools-drawer-width);
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  background-color: transparent;
}

.panel-tools__full-views,
.panel-tools__full-view {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.panel-tools__full-view {
  height: 100%;
}

.panel-tools__full-view {
  display: none;
}
.panel-tools__full-view--active {
  display: block;
}

:host([presentation='fullscreen']) {
  height: 100vh;
  inset: 0;
  position: fixed;
  width: 100vw;
  z-index: var(--dimension-z-index-modal);
}

:host([presentation='fullscreen']) .panel-tools__rail {
  display: none;
}
:host([presentation='fullscreen']) .panel-tools__drawer {
  max-width: 100vw;
  transition: none;
  width: 100vw;
}
:host([presentation='fullscreen']) .panel-tools__drawer--visible {
  border-left: none;
}
:host([presentation='fullscreen']) .panel-tools__drawer-surface {
  background-color: transparent;
  width: 100vw;
}

/*
 * A framework can update the reflected presentation attribute before Stencil
 * commits the matching render class. Conceal that single inconsistent frame so
 * slotted master/detail content never paints against the previous geometry.
 */
:host([presentation='fullscreen']:not(.panel-tools--fullscreen)) .panel-tools__drawer-surface,
:host(.panel-tools--fullscreen:not([presentation='fullscreen'])) .panel-tools__drawer-surface {
  visibility: hidden;
}

:host(.panel-tools--presentation-snap) .panel-tools__drawer {
  transition: none;
}

.panel-tools__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  position: relative;
}

/* Skip paint while fully closed; cleared during open/close motion for WYSIWYG slide. */
:host(.panel-tools--drawer-resting) .panel-tools__body {
  content-visibility: auto;
  contain-intrinsic-size: auto 200px;
}

.panel-tools__view {
  display: none;
  height: 100%;
  min-height: 0;
  box-sizing: border-box;
  padding: var(--dimension-space-200);
  color: var(--color-foreground-secondary);
  font-family: var(--typography-font-family-ui);
  font-size: var(--typography-fontsize-md);
  line-height: var(--typography-lineheight-md);
  font-weight: var(--typography-weight-regular);
  letter-spacing: var(--typography-letterspacing-negative-half);
}

.panel-tools__view ::slotted(*) {
  margin: 0;
  color: var(--color-foreground-secondary);
  font-family: var(--typography-font-family-ui);
  font-size: var(--typography-fontsize-md);
  line-height: var(--typography-lineheight-md);
  font-weight: var(--typography-weight-regular);
  letter-spacing: var(--typography-letterspacing-negative-half);
}

.panel-tools__view--active {
  display: block;
}

/* Rail — mirrors collapsed panel-nav width, padding, and item rhythm. */
.panel-tools__rail {
  flex: 0 0 var(--_panel-tools-rail-width);
  width: var(--_panel-tools-rail-width);
  min-width: var(--_panel-tools-rail-width);
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  height: 100%;
  min-height: 0;
  border-left: var(--_panel-tools-border);
  background-color: transparent;
}

.panel-tools__rail-header {
  justify-content: center;
  height: var(--dimension-size-600);
  flex-shrink: 0;
  border-bottom: var(--_panel-tools-border);
}

.panel-tools__rail-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-tools__rail-actions {
  display: flex;
  flex-direction: column;
  gap: var(--dimension-space-050);
  width: 100%;
}

/* Flush to the bottom of the rail — mirrors Search header pin. */
.panel-tools__rail-footer {
  justify-content: center;
  flex-shrink: 0;
  margin-top: auto;
}

.panel-tools__rail-action {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--ds-control-height);
  flex-shrink: 0;
}

@media (prefers-reduced-motion: reduce) {
  :host(.panel-tools--ready.panel-tools--motion-opening) .panel-tools__drawer,
  :host(.panel-tools--ready.panel-tools--motion-closing) .panel-tools__drawer {
    transition: none;
  }
}
